import {
  isAnnouncementVisible,
  useAnnouncements,
  type AnnouncementLevel,
} from './useAnnouncements';

/**
 * 未読・有効な banner 型お知らせを 1 件のみ表示。
 * 旧 AnnouncementHost.tsx (L3 参照) の banner 部分を踏襲、modal は Phase 5。
 *
 * level → 色:
 *   info     → info-600 (青)
 *   warning  → warning-600 (橙)
 *   critical → danger-700 (赤、強調)
 */

const LEVEL_BG: Record<AnnouncementLevel, string> = {
  info: 'bg-info-600',
  warning: 'bg-warning-600',
  critical: 'bg-danger-700',
};

export interface AnnouncementBannerProps {
  /**
   * 現在地区 ID。この地区の announcements を購読する (必須)。
   * 省略すると fallback phase2-default を読み、実地区のお知らせが配信されない不具合になる。
   */
  districtId: string;
  /**
   * 現セッション ID。Announcement.targetSessionIds の filter に渡す (user 第 2 弾、REQ-4.3.33)。
   * null = 未選択 (targetSessionIds 非空時は弾かれて表示されない)。
   */
  currentSessionId?: string | null;
}

export function AnnouncementBanner({
  districtId,
  currentSessionId = null,
}: AnnouncementBannerProps) {
  const { state, seenIds, markSeen } = useAnnouncements(districtId);

  if (state.status !== 'ready') return null;

  const banner = state.items.find(
    (a) =>
      a.pattern === 'banner' && isAnnouncementVisible(a, seenIds, new Date(), currentSessionId),
  );
  if (!banner) return null;

  // old2 AnnouncementHost 逐語: banner は固定オーバーレイ配置 (呼出元の relative 親に対して
  // absolute top z-30、pointer-events-none の wrapper + banner 自体は pointer-events-auto)。
  // これにより下のコンテンツの上に浮かせつつ、banner 外のタップは下層に透過させる。
  return (
    <div className="absolute top-0 left-0 right-0 z-30 space-y-1 p-2 pointer-events-none">
      <div
        className={`${LEVEL_BG[banner.level]} text-white px-4 py-3 rounded shadow flex items-start gap-3 pointer-events-auto`}
        role="status"
      >
        <div className="flex-1 min-w-0">
          <p className="font-bold text-sm">📣 {banner.title}</p>
          {banner.body && <p className="text-sm whitespace-pre-wrap mt-1">{banner.body}</p>}
        </div>
        <button
          type="button"
          onClick={() => markSeen(banner.id)}
          className="text-white text-xl px-2 min-h-touch min-w-touch shrink-0 hover:bg-white/10 rounded"
          aria-label="閉じる"
        >
          ×
        </button>
      </div>
    </div>
  );
}
